import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, CheckCircle2, Landmark, Info } from "lucide-react";
import api, { API } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EbrcTracker, SelfCertifyGuide } from "@/pages/EbrcTracker";
import RbiBadge from "@/components/RbiBadge";
import { TXN } from "@/constants/testIds";

const inr = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(v);
const usd = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v);

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  const loadTxn = async () => {
    try {
      const { data } = await api.get(`/transactions/${id}`);
      setTxn(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load transaction.");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTxn();
  }, [id]);

  // Explicit user confirmation that they've completed self-certification on the
  // DGFT portal. Jumps eBRC status → `self_certified`. Replaces the removed
  // "Simulate: ..." button flow.
  const confirmSelfCertified = async () => {
    setAdvancing(true);
    try {
      const { data } = await api.post(`/transactions/${id}/ebrc/self-certify`);
      toast.success("Marked as self-certified — thanks for closing the loop.");
      // eslint-disable-next-line no-unused-vars
      const _ = data;
      await loadTxn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record self-certification.");
    } finally {
      setAdvancing(false);
    }
  };

  const downloadEfira = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("sf_token");
      const res = await fetch(`${API}/transactions/${id}/efira`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Flo Payments-eFIRA-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("eFIRA downloaded");
    } catch (err) {
      toast.error("Could not download eFIRA");
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !txn) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading transaction…
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={TXN.root}>
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto flex items-center gap-2 p-4 md:p-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid={TXN.backBtn}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 md:p-10 space-y-6">
        <RbiBadge />
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-[#E5F4EC] text-[#1E8A5B] border-[#1E8A5B]/30 rounded-full hover:bg-[#E5F4EC]">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Deposit received
            </Badge>
            {txn.withdrawal_status === "complete" ? (
              <Badge className="bg-[#E5F4EC] text-[#1E8A5B] border-[#1E8A5B]/30 rounded-full hover:bg-[#E5F4EC]">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Withdrawn
              </Badge>
            ) : (
              <Badge className="bg-[#FBF1E1] text-[#8F5A16] border-[#C77F1E]/30 rounded-full hover:bg-[#FBF1E1]">
                Awaiting withdrawal
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-1">Payment received from</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">
            {txn.sender_name}
          </h1>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-6 pb-6 border-b">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Sent</p>
                <p className="font-display text-3xl md:text-4xl font-bold text-primary sf-mono">{usd(txn.amount_usd)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Received</p>
                <p className="font-display text-3xl md:text-4xl font-bold text-[#16a34a] sf-mono">{inr(txn.amount_inr)}</p>
              </div>
            </div>

            <div className="pt-6 space-y-4">
              <DetailRow label="Sender" value={`${txn.sender_name} (${txn.sender_country_name || txn.sender_country || "—"})`} />
              <DetailRow
                label="Exchange rate applied"
                value={`₹${txn.fx_rate.toFixed(4)} / $`}
                sublabel="(Google mid-market rate — no markup)"
                mono
              />
              <DetailRow
                label="Settlement date"
                value={new Date(txn.settlement_date).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              />
              <DetailRow label="Transaction ID" value={txn.id} mono />
              <DetailRow label="Status" value="Completed" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-bold text-primary">Foreign Inward Remittance Advice</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Download the eFIRA for GST refund claims &amp; RBI reporting.
              </p>
            </div>
            {txn.withdrawal_status !== "complete" ? (
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button
                          disabled
                          className="h-12 px-6 rounded-lg font-semibold opacity-60 cursor-not-allowed"
                          data-testid={TXN.efiraBtn}
                        >
                          <Download className="h-4 w-4 mr-2" /> Download eFIRA
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="max-w-xs text-xs">Complete withdrawal to generate eFIRA.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  onClick={() => navigate(`/transactions/${id}/withdraw`)}
                  className="h-12 px-6 rounded-lg font-semibold bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  data-testid={TXN.withdrawBtn}
                >
                  <Landmark className="h-4 w-4 mr-2" /> Withdraw to bank
                </Button>
              </div>
            ) : (
              <Button
                onClick={downloadEfira}
                disabled={downloading}
                className="h-12 px-6 rounded-lg font-semibold"
                data-testid={TXN.efiraBtn}
              >
                {downloading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing…
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" /> Download eFIRA
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {txn.withdrawal_status !== "complete" && (
          <div className="flex gap-3 rounded-lg bg-muted p-4 border">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Upload your sales invoice to trigger the payout to your linked bank account. Once complete, your eFIRA
              becomes downloadable.
            </p>
          </div>
        )}

        <EbrcTracker
          status={txn.ebrc_status || "payment_received"}
        />

        {["irm_generated", "self_cert_pending", "self_certified"].includes(
          txn.ebrc_status || "payment_received"
        ) && (
          <SelfCertifyGuide
            dgft={txn.withdrawal?.dgft}
            status={txn.ebrc_status || "payment_received"}
            onSelfCertify={confirmSelfCertified}
            certifying={advancing}
          />
        )}
      </main>
    </div>
  );
}

function DetailRow({ label, value, mono, sublabel }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 py-2">
      <span className="text-sm text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
      <div className="text-right">
        <span className={`text-base font-medium text-foreground break-all ${mono ? "sf-mono" : ""}`}>{value}</span>
        {sublabel && (
          <span className="block text-[10px] text-muted-foreground/80 mt-0.5 font-medium">{sublabel}</span>
        )}
      </div>
    </div>
  );
}
